import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type {
  Application,
  ApplicationProofPaths,
  ExposureCut,
  FindingProof,
  ProofHop,
  Reach,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { EDGE_KIND, NODE_TYPE } from '../../lib/cloud-graph';
import { connector, layoutCanvas, NODE_H, NODE_W, type CanvasNode } from '../../lib/proof-canvas';

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

const REACH: Record<Reach, { label: string; cls: string }> = {
  SECRETS: { label: 'Reaches a secret', cls: 'reach-secrets' },
  DATA: { label: 'Reaches data', cls: 'reach-data' },
  COMPUTE: { label: 'Reaches compute', cls: 'reach-compute' },
  EDGE_ONLY: { label: 'Stops at the edge', cls: 'reach-edge' },
  NOT_IN_ACCOUNT: { label: 'Not in the account', cls: 'reach-none' },
};

type Tab = 'canvas' | 'findings' | 'resources';
type Combine = 'path' | 'finding';

/**
 * Proof paths (ADR 0009) as an attack-path canvas (UI direction B).
 *
 * The canvas reads left to right: the internet, what the scan observed, then each resource the
 * request — or the identity serving it — reaches, every edge labelled with how it was proved.
 * "All paths" merges a finding's paths and fans out where they part. Selecting a hop shows its
 * evidence. The cuts beside it come from Exposure (ADR 0010): the hops on these paths whose
 * removal pays down the most. Nothing is drawn that the graph cannot prove.
 */
export function ProofPathsView({ application }: { application: Application }) {
  const paths = useQuery({
    queryKey: ['proofPaths', application.id],
    queryFn: () => api.getProofPaths(application.id),
    refetchInterval: 60_000,
  });
  const exposure = useQuery({ queryKey: ['exposure'], queryFn: api.getExposure });

  if (paths.isLoading) return <p className="muted">Tracing findings through the account…</p>;
  if (paths.isError || !paths.data) {
    return (
      <p className="login-error" role="alert">
        {paths.error instanceof ApiError ? paths.error.message : 'Could not trace the findings.'}
      </p>
    );
  }
  if (paths.data.unavailable) {
    return (
      <section className="card panel">
        <h2 className="panel-title">Proof paths</h2>
        <p className="muted">{paths.data.unavailable}</p>
        <p className="muted">
          Link the application to its cloud account on the <strong>Cloud architecture</strong> tab
          and run a discovery; findings from its scans are then traced through it.
        </p>
      </section>
    );
  }
  return <ProofPathsWorkspace data={paths.data} cuts={exposure.data?.cuts ?? []} />;
}

export function ProofPathsWorkspace({
  data,
  cuts,
}: {
  data: ApplicationProofPaths;
  cuts: readonly ExposureCut[];
}) {
  const [tab, setTab] = useState<Tab>('canvas');
  const [selected, setSelected] = useState<string | null>(null);

  const focus = data.items.find((i) => i.finding.id === selected) ?? data.items[0];
  const pathCount = data.items.reduce((n, i) => n + i.paths.length, 0);
  const resources = useMemo(() => resourcesOf(data.items), [data.items]);
  // Only the cuts that lie on this application's paths.
  const here = new Set(resources.map((r) => r.nodeId));
  const localCuts = cuts.filter((c) => here.has(c.nodeId));

  return (
    <>
      <div className="kpis surface-kpis">
        <div className="kpi kpi-danger">
          <span>Reach a secret</span>
          <span>{data.summary.reachSecrets}</span>
        </div>
        <div className="kpi">
          <span>Reach data</span>
          <span>{data.summary.reachData}</span>
        </div>
        <div className="kpi">
          <span>Not in the account</span>
          <span>{data.summary.notInAccount}</span>
        </div>
        <div className="kpi kpi-verified">
          <span>Closed by a proved fix</span>
          <span>{data.summary.closed}</span>
        </div>
      </div>

      <p className="muted surface-basis" role="status">
        Traced through the discovery of{' '}
        {date(data.discovery!.finishedAt ?? data.discovery!.requestedAt)}. Reach is what
        configuration allows — evidence, not proof of exploitation.
        {data.integrity === 'changed' ? (
          <strong className="sev-high">
            {' '}
            The stored discovery no longer matches its recorded hash; do not rely on these paths.
          </strong>
        ) : null}
      </p>

      {data.items.length === 0 || !focus ? (
        <section className="card panel">
          <p className="muted">No findings to trace yet. Run a passive scan of this application.</p>
        </section>
      ) : (
        <section className="card panel pp" aria-label="Proof paths">
          <div className="pp-tabs" role="tablist" aria-label="Proof path views">
            {(
              [
                ['canvas', `Proof paths (${pathCount})`],
                ['findings', `Findings (${data.items.length})`],
                ['resources', `Resources (${resources.length})`],
              ] as const
            ).map(([t, label]) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`pp-tab${tab === t ? ' is-on' : ''}`}
                onClick={() => setTab(t)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'canvas' ? (
            <CanvasPanel
              key={focus.finding.id}
              item={focus}
              items={data.items}
              cuts={localCuts}
              onPick={setSelected}
            />
          ) : tab === 'findings' ? (
            <FindingList
              items={data.items}
              focus={focus.finding.id}
              onPick={(id) => {
                setSelected(id);
                setTab('canvas');
              }}
            />
          ) : (
            <ResourceTable resources={resources} />
          )}
        </section>
      )}
    </>
  );
}

function CanvasPanel({
  item,
  items,
  cuts,
  onPick,
}: {
  item: FindingProof;
  items: readonly FindingProof[];
  cuts: readonly ExposureCut[];
  onPick: (findingId: string) => void;
}) {
  const [combine, setCombine] = useState<Combine>('finding');
  const [pathIndex, setPathIndex] = useState(0);
  // Bumping the run remounts the canvas, which restarts the hop-by-hop replay.
  const [run, setRun] = useState(0);
  const shown = combine === 'finding' ? item.paths : item.paths.slice(pathIndex, pathIndex + 1);
  const canvas = useMemo(() => layoutCanvas(shown), [shown]);
  const defaultNode = canvas.nodes.filter((n) => n.target).at(0) ?? canvas.nodes.at(-1);
  const [picked, setPicked] = useState<string | null>(null);
  const node = canvas.nodes.find((n) => n.key === picked) ?? defaultNode;
  const cutKeys = new Set(cuts.map((c) => c.nodeId));

  return (
    <div className="pp-body">
      <div className="pp-toolbar">
        <div className="pp-title">
          <label className="sr-only" htmlFor="pp-finding">
            Finding
          </label>
          <select
            id="pp-finding"
            className="input pp-select"
            value={item.finding.id}
            onChange={(e) => onPick(e.target.value)}
          >
            {items.map((i) => (
              <option key={i.finding.id} value={i.finding.id}>
                {i.finding.severity} · {i.finding.title}
              </option>
            ))}
          </select>
          <span className="muted pp-count">
            <span className="mono">{item.host}</span> ·{' '}
            {item.closed ? 'closed by a proved fix' : REACH[item.reach].label.toLowerCase()}
            {item.reachable.secrets + item.reachable.dataStores > 0
              ? ` · ${item.reachable.secrets} secret${item.reachable.secrets === 1 ? '' : 's'}, ${item.reachable.dataStores} data store${item.reachable.dataStores === 1 ? '' : 's'} in all`
              : ''}
          </span>
        </div>
        <div className="pp-actions">
          <label className="sr-only" htmlFor="pp-combine">
            Paths to draw
          </label>
          <select
            id="pp-combine"
            className="input pp-select"
            value={combine}
            onChange={(e) => {
              setCombine(e.target.value as Combine);
              setPicked(null);
            }}
          >
            <option value="finding">Combine paths ({item.paths.length})</option>
            <option value="path">One path at a time</option>
          </select>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setRun(run + 1)}>
            Replay ▶
          </button>
          <Link
            className="btn btn-outline btn-sm"
            to={`/app/engagements/${item.finding.engagementId}`}
          >
            Open the finding
          </Link>
        </div>
      </div>

      {combine === 'path' && item.paths.length > 1 ? (
        <div className="filters" role="tablist" aria-label="Paths">
          {item.paths.map((p, i) => (
            <button
              key={p.target.nodeId}
              type="button"
              role="tab"
              className="filter"
              aria-selected={i === pathIndex}
              aria-pressed={i === pathIndex}
              onClick={() => {
                setPathIndex(i);
                setPicked(null);
                setRun(run + 1);
              }}
            >
              → {NODE_TYPE[p.target.type].label}: {p.target.name}
            </button>
          ))}
        </div>
      ) : null}

      {item.closed ? (
        <p className="proof-closed" role="status">
          <strong>Path closed at hop 1.</strong> The finding was proved fixed
          {item.closed.proofScanId ? (
            <>
              {' '}
              by re-scan <span className="mono">{item.closed.proofScanId.slice(0, 8)}</span>
            </>
          ) : null}
          {item.closed.at ? ` on ${date(item.closed.at)}` : ''}. What lay behind it is faded.
        </p>
      ) : null}

      <div className="pp-canvas-wrap">
        <svg
          key={run}
          className={`pp-canvas${item.closed ? ' is-closed' : ''}`}
          width={Math.max(canvas.width, 640)}
          height={canvas.height}
          viewBox={`0 0 ${Math.max(canvas.width, 640)} ${canvas.height}`}
          role="img"
          aria-label={describe(item, canvas.nodes)}
        >
          <defs>
            <marker
              id="pp-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 z" className="pp-arrowhead" />
            </marker>
          </defs>
          {canvas.edges.map((e) => {
            const from = canvas.nodes.find((n) => n.key === e.from)!;
            const to = canvas.nodes.find((n) => n.key === e.to)!;
            const hot = to.target && to.hop.kind === 'RESOURCE' && to.hop.type === 'SECRET';
            return (
              <g
                key={`${e.from}>${e.to}`}
                className="pp-edge-g"
                style={{ animationDelay: `${to.col * 380}ms` }}
              >
                <path
                  d={connector(from, to)}
                  className={`pp-edge${hot ? ' is-hot' : ''}`}
                  markerEnd="url(#pp-arrow)"
                />
                {e.via ? (
                  <text
                    className="pp-edge-label"
                    x={(from.x + NODE_W + to.x) / 2}
                    y={(from.y + to.y) / 2 + NODE_H / 2 - 8}
                    textAnchor="middle"
                  >
                    {EDGE_KIND[e.via.edge].label}
                  </text>
                ) : null}
              </g>
            );
          })}
          {canvas.nodes.map((n) => (
            <HopNode
              key={n.key}
              node={n}
              selected={node?.key === n.key}
              cut={cutKeys.has(n.key)}
              onSelect={() => setPicked(n.key)}
            />
          ))}
        </svg>
        {item.reach === 'NOT_IN_ACCOUNT' ? (
          <p className="pp-unproved" role="status">
            <span className="mono">{item.host}</span> does not resolve to the linked account.
            Nothing behind it can be proved, so nothing is drawn.
          </p>
        ) : null}
      </div>

      <div className="pp-lower">
        <HopEvidence node={node} />
        <section className="pp-cuts" aria-labelledby="pp-cuts">
          <h3 id="pp-cuts" className="xp-eyebrow">
            Prioritize · cuts on these paths
          </h3>
          {cuts.length === 0 ? (
            <p className="muted">
              No hop on this application&rsquo;s paths leads on to data or a secret, so there is
              nothing to cut.
            </p>
          ) : (
            <ol className="pp-cut-list">
              {cuts.map((c) => (
                <li key={c.nodeId}>
                  <span className="xp-stripe xp-stripe-hot" aria-hidden="true" />
                  <div>
                    <div className="pp-cut-name">
                      Cut <span className="mono">{c.name}</span>
                    </div>
                    <div className="xp-meta">
                      {NODE_TYPE[c.type].label} · {c.findings}{' '}
                      {c.findings === 1 ? 'finding stops' : 'findings stop'} reaching{' '}
                      {c.reaches.join(', ')}
                    </div>
                  </div>
                  <span className="xp-val xp-neg">−{fmt(c.debtReduction)}</span>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={!canvas.nodes.some((n) => n.key === c.nodeId)}
                    onClick={() => setPicked(c.nodeId)}
                  >
                    Show
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}

function HopNode({
  node,
  selected,
  cut,
  onSelect,
}: {
  node: CanvasNode;
  selected: boolean;
  cut: boolean;
  onSelect: () => void;
}) {
  const { hop } = node;
  const style = { animationDelay: `${node.col * 380}ms` };
  const kind =
    hop.kind === 'INTERNET'
      ? 'pp-internet'
      : hop.kind === 'OBSERVED'
        ? 'pp-observed'
        : `cat-${NODE_TYPE[hop.type].category}${hop.type === 'SECRET' ? ' is-secret' : ''}`;
  const title =
    hop.kind === 'INTERNET'
      ? 'Internet'
      : hop.kind === 'OBSERVED'
        ? `${hop.method ?? 'GET'} ${pathOf(hop.url)}`
        : hop.name;
  const sub =
    hop.kind === 'INTERNET'
      ? 'Where the request starts'
      : hop.kind === 'OBSERVED'
        ? `Observed by scan ${hop.scanId.slice(0, 8)}`
        : `${NODE_TYPE[hop.type].label}${hop.public ? ' · public' : ''}`;
  return (
    <g
      className={`pp-node ${kind}${selected ? ' is-selected' : ''}${cut ? ' is-cut' : ''}${node.target ? ' is-target' : ''}`}
      style={style}
      transform={`translate(${node.x},${node.y})`}
      tabIndex={0}
      role="button"
      aria-label={`${title}, ${sub}`}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <rect className="pp-box" width={NODE_W} height={NODE_H} rx={10} />
      <text className="pp-node-title" x={12} y={23}>
        {clip(title, 20)}
      </text>
      <text className="pp-node-sub" x={12} y={42}>
        {clip(sub, 24)}
      </text>
      {node.target && hop.kind === 'RESOURCE' ? (
        <circle className="pp-dot" cx={NODE_W - 6} cy={6} r={5} />
      ) : null}
      {cut ? (
        <text className="pp-cut-mark" x={NODE_W - 10} y={NODE_H - 8} textAnchor="end">
          ✂
        </text>
      ) : null}
    </g>
  );
}

function HopEvidence({ node }: { node: CanvasNode | undefined }) {
  if (!node) return null;
  const { hop } = node;
  return (
    <section className="pp-evidence" aria-labelledby="pp-evidence">
      <h3 id="pp-evidence" className="xp-eyebrow">
        Hop evidence
      </h3>
      {hop.kind === 'INTERNET' ? (
        <p className="muted">Anyone on the internet. Every path starts here.</p>
      ) : hop.kind === 'OBSERVED' ? (
        <dl className="pp-dl">
          <dt>request</dt>
          <dd className="mono">
            {hop.method ?? 'GET'} {hop.url}
          </dd>
          <dt>scan</dt>
          <dd className="mono">{hop.scanId}</dd>
          <dt>last seen</dt>
          <dd>{date(hop.lastSeenAt)}</dd>
        </dl>
      ) : (
        <dl className="pp-dl">
          <dt>resource</dt>
          <dd>
            {hop.name} <span className="muted">· {NODE_TYPE[hop.type].label}</span>
            {hop.public ? <span className="cloud-public"> · public</span> : null}
          </dd>
          <dt>id</dt>
          <dd className="mono">{hop.nodeId}</dd>
          {hop.via ? (
            <>
              <dt>reached by</dt>
              <dd>{EDGE_KIND[hop.via.edge].label}</dd>
              <dt>api</dt>
              <dd className="mono">{hop.via.api}</dd>
              <dt>field</dt>
              <dd className="mono">{hop.via.field}</dd>
              {hop.via.detail ? (
                <>
                  <dt>detail</dt>
                  <dd className="mono">{hop.via.detail}</dd>
                </>
              ) : null}
            </>
          ) : (
            <>
              <dt>reached by</dt>
              <dd>The host answers on this resource.</dd>
            </>
          )}
        </dl>
      )}
    </section>
  );
}

function FindingList({
  items,
  focus,
  onPick,
}: {
  items: readonly FindingProof[];
  focus: string;
  onPick: (id: string) => void;
}) {
  return (
    <ul className="proof-finding-list pp-findings">
      {items.map((item) => (
        <li key={item.finding.id}>
          <button
            type="button"
            className={`proof-finding${focus === item.finding.id ? ' is-selected' : ''}`}
            aria-pressed={focus === item.finding.id}
            onClick={() => onPick(item.finding.id)}
          >
            <span className={`sev-${item.finding.severity.toLowerCase()}`}>
              {item.finding.severity}
            </span>
            <span className="proof-finding-title">{item.finding.title}</span>
            {item.closed ? (
              <span className="reach reach-closed">Closed</span>
            ) : (
              <span className={`reach ${REACH[item.reach].cls}`}>{REACH[item.reach].label}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

interface ResourceRow {
  nodeId: string;
  name: string;
  type: Extract<ProofHop, { kind: 'RESOURCE' }>['type'];
  public: boolean;
  findings: number;
}

function resourcesOf(items: readonly FindingProof[]): ResourceRow[] {
  const rows = new Map<string, ResourceRow & { seen: Set<string> }>();
  for (const item of items) {
    for (const path of item.paths) {
      for (const hop of path.hops) {
        if (hop.kind !== 'RESOURCE') continue;
        const row = rows.get(hop.nodeId) ?? {
          nodeId: hop.nodeId,
          name: hop.name,
          type: hop.type,
          public: hop.public,
          findings: 0,
          seen: new Set<string>(),
        };
        if (!row.seen.has(item.finding.id)) {
          row.seen.add(item.finding.id);
          row.findings += 1;
        }
        rows.set(hop.nodeId, row);
      }
    }
  }
  return [...rows.values()]
    .map(({ seen: _seen, ...r }) => r)
    .sort((a, b) => b.findings - a.findings || a.name.localeCompare(b.name));
}

function ResourceTable({ resources }: { resources: readonly ResourceRow[] }) {
  if (resources.length === 0) return <p className="muted">No resource lies on any path yet.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Resource</th>
            <th>Type</th>
            <th>Public</th>
            <th className="num">Findings through it</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((r) => (
            <tr key={r.nodeId}>
              <td className="mono">{r.name}</td>
              <td>{NODE_TYPE[r.type].label}</td>
              <td>
                {r.public ? (
                  <span className="cloud-public">public</span>
                ) : (
                  <span className="muted">no</span>
                )}
              </td>
              <td className="num mono">{r.findings}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname || '/';
  } catch {
    return url;
  }
}

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

function describe(item: FindingProof, nodes: readonly CanvasNode[]): string {
  const names = nodes.map((n) =>
    n.hop.kind === 'INTERNET'
      ? 'internet'
      : n.hop.kind === 'OBSERVED'
        ? 'observed request'
        : n.hop.name,
  );
  return `Proof path for ${item.finding.title}: ${names.join(', then ')}`;
}
